import { ComponentProps, useEffect, useMemo, useRef, useState } from "react";
import { FCX, ID } from "@datassential/platform-ui-lib";
import FoodTabs from "@pages/Food/components/Tabs/Tabs";
import { ISocialPost, SocialPosts } from "@models/SocialPosts";
import ExpandableSocialPostCard from "@components/SocialPostCard/ExpandableSocialPostCard";
import CopyButton from "@components/new/Table/CopyButton";
import CopyNotification from "@components/new/Table/CopyNotification";
import useNotification from "@hooks/useNotification";
import { trimInnerHtml, trimLineBreaks } from "@helpers";
import './FoodiesExamplePosts.scss';

enum SortFilter {
    Engagement = 0,
    Newest = 1,
}

const SORT_TABS = [
    { id: SortFilter.Engagement, title: 'Engagement' },
    { id: SortFilter.Newest, title: 'Newest' },
];

const COPY_DATA_CONFIG: Array<{
    key: keyof ISocialPost;
    title: string;
    valueFormatter?: (value: string) => string;
}> = [
    { key: 'source', title: 'Source' },
    { key: 'engagement', title: 'Engagement Count' },
    { key: 'date', title: 'Date', valueFormatter: SocialPosts.formatDate },
    { key: 'text', title: 'Description', valueFormatter: (value) => trimLineBreaks(trimInnerHtml(value)) },
    { key: 'url', title: 'Post URL' },
];

const FoodiesExamplePosts: FCX<{
    data: ISocialPost[];
    isBlocked?: boolean;
    selectedThemeId?: number | null;
}> = ({ data, isBlocked, selectedThemeId }) => {
    const [sortFilter, setSortFilter] = useState<SortFilter>(SortFilter.Engagement);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const notification = useNotification();

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
    }, [selectedThemeId]);

    const sortedData = useMemo(() => {
        const sorted = [...data];
        if (sortFilter === SortFilter.Newest) {
            sorted.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        } else {
            sorted.sort((a, b) => b.engagement - a.engagement);
        }
        return sorted;
    }, [data, sortFilter]);

    const copyData = useMemo(
        (): ComponentProps<typeof CopyButton>['data'] => [
            COPY_DATA_CONFIG.map(({ title }) => title),
            ...sortedData.map(post =>
                COPY_DATA_CONFIG.map(({ key, valueFormatter }) => {
                    const value = post[key];
                    return valueFormatter ? valueFormatter(`${value}`) : `${value}`;
                })
            ),
        ],
        [sortedData],
    );

    return (
        <div className="FoodiesExamplePosts" data-testid="blockFoodiesExamplePosts">
            <div className="FoodiesExamplePosts__header">
                <div className="FoodiesExamplePosts__header-left">
                    <span className="FoodiesExamplePosts__header-title">EXAMPLE POSTS</span>
                    <span className="FoodiesExamplePosts__header-subtitle">
                        by engagement, past 30 days
                    </span>
                </div>
                {!isBlocked && (
                    <div className="FoodiesExamplePosts__header-right">
                        <FoodTabs
                            data={SORT_TABS}
                            activeTabId={sortFilter}
                            onItemClick={(id: ID) => setSortFilter(id as SortFilter)}
                            testId="blockFoodiesExamplePostsTabs"
                        />
                        <div onClick={notification.handleShowNotification}>
                            <CopyButton
                                data={copyData}
                                testId="buttonFoodiesExamplePostsCopy"
                            />
                        </div>
                    </div>
                )}
            </div>
            <div ref={scrollRef} className="FoodiesExamplePosts__scroll">
                <div key={`${sortFilter}-${selectedThemeId}`} className="FoodiesExamplePosts__grid appears del-1">
                    {sortedData.map((post) => (
                        <ExpandableSocialPostCard
                            key={post.id}
                            post={post}
                            scrollRef={scrollRef}
                            className="FoodiesExamplePosts__card"
                            expandedClassName="FoodiesExamplePosts__card-expanded"
                            testIdPrefix="blockFoodiesPostCard"
                        />
                    ))}
                </div>
            </div>
            <CopyNotification isOpen={notification.isOpen}/>
        </div>
    );
};

export default FoodiesExamplePosts;
